import { MenuItem, styled, TextField, Tooltip, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import WmCompass from '../../assets/wm_compass_new.png';
import { getConversations } from '../../redux/actions/conversationActions';
import { getFolders } from '../../redux/actions/folderActions';
import { myFolder } from '../../redux/consts';
import {
  selectAppStatus,
  setIsSwitchingApps,
  switchApp,
} from '../../redux/reducers/appStatusReducer';
import { setSearchQuery as setConversationsSearchQuery } from '../../redux/reducers/conversationReducer';
import {
  selectFolders,
  setCurrentFolder,
  setSearchQuery as setFoldersSearchQuery,
} from '../../redux/reducers/folderReducer';
import { AppDispatch } from '../../redux/store';
import { setFocusById } from '../../services/util';
import { AppEnum, AppNameBannerMap } from '../../Types/enums';
import { AnalyticsEvent, useAnalytics } from '../Providers/AnalyticsProvider';
import { useFeatures } from '../Providers/FeatureProvider';
import { useProjects } from '../Providers/ProjectsProvider';
import DrawerToggleButton from './DrawerToggleButton';

const AI_MOMENTUM_URL = 'https://ai-momentum.westmonroe-cloud.com/';

type DrawerAppOption = {
  value: AppEnum;
  featureFlag: boolean;
  onClick?: () => void;
};

const DrawerAppsContainer = styled('div')<{ drawerOpen: boolean }>(({ drawerOpen }) => ({
  display: 'flex',
  width: '100%',
  alignItems: drawerOpen ? 'center' : 'center',
  flexDirection: drawerOpen ? 'row' : 'column',
  gap: '5px',
  paddingBottom: '10px',
  justifyContent: drawerOpen ? 'flex-start' : 'center',
}));

const StyledMenuItem = styled(MenuItem)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'start',
});

const DrawerApps = ({ handleDrawerToggle }: { handleDrawerToggle: () => void }): JSX.Element => {
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const { selectedApp, drawerOpen } = useSelector(selectAppStatus);
  const { currentFolder } = useSelector(selectFolders);
  const dispatch = useDispatch<AppDispatch>();
  const features = useFeatures();
  const { setShouldFetch } = useProjects();
  const location = useLocation();
  const libraryUrl = import.meta.env.VITE_LIBRARY_URL;

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [isLogoHovered, setIsLogoHovered] = useState(false);

  const handleAppSelection = async (appName: AppEnum = AppEnum.NIGEL) => {
    if (appName === AppEnum.AI_STORE) {
      if (libraryUrl) {
        window.open(libraryUrl, '_blank', 'noopener,noreferrer');
      }
    } else if (appName === AppEnum.DEAL_GPT) {
      navigate('/intellio-advantage');
    } else if (appName === AppEnum.INTELLIO_INSIGHTS) {
      navigate('/intellio-insights');
    } else if (appName === AppEnum.AI_MOMENTUM) {
      window.open(AI_MOMENTUM_URL, '_blank', 'noopener,noreferrer');
    } else {
      navigate('/folder/chat?ui=classic');
      setFocusById('chat-input');
    }
  };

  useEffect(() => {
    (async () => {
      const isIntellio =
        location.pathname.includes('intellio-advantage') ||
        location.pathname.includes('question-library');

      const isIntellioInsights = location.pathname.includes('intellio-insights');

      const isChatFolder = location.pathname.includes('folder/chat');

      if (isIntellio && selectedApp !== AppEnum.DEAL_GPT) {
        dispatch(setIsSwitchingApps(true));
        dispatch(setFoldersSearchQuery(''));
        dispatch(setConversationsSearchQuery(''));
        await dispatch(switchApp(AppEnum.DEAL_GPT));

        try {
          await dispatch(getFolders({ foldersWithProjects: true }));

          await dispatch(getConversations({ pageAction: 'reset', searchQuery: '' }));
        } catch (error) {
          enqueueSnackbar('Failed to fetch folders', { variant: 'error' });
        } finally {
          dispatch(setIsSwitchingApps(false));
          window.dispatchEvent(new Event('resize'));
        }
      } else if (isIntellioInsights && selectedApp !== AppEnum.INTELLIO_INSIGHTS) {
        dispatch(setIsSwitchingApps(true));
        await dispatch(switchApp(AppEnum.INTELLIO_INSIGHTS));
        dispatch(setIsSwitchingApps(false));
        window.dispatchEvent(new Event('resize'));
      } else if (isChatFolder && selectedApp !== AppEnum.NIGEL) {
        dispatch(setIsSwitchingApps(true));
        dispatch(setFoldersSearchQuery(''));
        dispatch(setConversationsSearchQuery(''));
        await dispatch(switchApp(AppEnum.NIGEL));
        await dispatch(getFolders());
        await dispatch(getConversations({ pageAction: 'reset', searchQuery: '' }));

        dispatch(setIsSwitchingApps(false));
        window.dispatchEvent(new Event('resize'));
      }
    })();
  }, [selectedApp, location.pathname, dispatch]);

  const appOptions = useMemo<DrawerAppOption[]>(
    () => [
      { value: AppEnum.NIGEL, featureFlag: true },
      ...(libraryUrl
        ? [
            {
              value: AppEnum.AI_STORE,
              featureFlag: true,
            },
          ]
        : []),
      { value: AppEnum.WM_EMPLOYEE_HANDBOOK, featureFlag: features.app_employee_handbook_access },
      {
        value: AppEnum.ENERGY_UTILITIES_DEMO,
        featureFlag: features.app_energy_utilities_demo_access,
      },
      {
        value: AppEnum.INTELLIO_INSIGHTS,
        featureFlag: true,
        onClick: () => setShouldFetch(true),
      },
      {
        value: AppEnum.AI_MOMENTUM,
        featureFlag: true,
      },
    ],
    [features, libraryUrl, setShouldFetch]
  );

  return (
    <DrawerAppsContainer drawerOpen={drawerOpen}>
      <div
        style={{ height: '24px', cursor: 'pointer', flexShrink: 0 }}
        onClick={() => {
          if (drawerOpen) {
            analytics.event(AnalyticsEvent.WM_LOGO_CLICK);
            navigate('/');
          } else {
            handleDrawerToggle();
          }
        }}
        onMouseEnter={() => {
          if (!drawerOpen) setIsLogoHovered(true);
        }}
        onMouseLeave={() => {
          if (!drawerOpen) setIsLogoHovered(false);
        }}
      >
        {!isLogoHovered ? (
          <img
            src={WmCompass}
            alt="West Monroe Compass"
            style={{ width: '24px', height: '24px' }}
          />
        ) : (
          <DrawerToggleButton
            isDrawerOpen={false}
            setIsDrawerOpen={handleDrawerToggle}
            tooltipCaption={'Open Panel'}
          />
        )}
      </div>
      {drawerOpen && (
        <Tooltip
          enterDelay={1000}
          open={tooltipOpen}
          title={
            <Typography variant="caption">
              The App Selected will determine the available features and functionalities.
            </Typography>
          }
          placement="left"
          sx={{ height: '40px' }}
          id={'app-select-description'}
        >
          <TextField
            id="app-select"
            sx={{
              width: '100%',
              '& > *': { fontFamily: 'Noto Sans' },
            }}
            value={selectedApp}
            onMouseEnter={() => setTooltipOpen(true)}
            onMouseLeave={() => setTooltipOpen(false)}
            onChange={async (event) => {
              const newValue = event.target.value as AppEnum;

              analytics.event(
                AnalyticsEvent.WM_APP_CHANGE,
                {
                  value: newValue,
                },
                {
                  oldApp: selectedApp,
                  newApp: newValue,
                }
              );

              await handleAppSelection(newValue);

              if (newValue === AppEnum.AI_STORE || newValue === AppEnum.AI_MOMENTUM) {
                setTooltipOpen(false);
                return;
              }

              if (newValue === AppEnum.DEAL_GPT) {
                dispatch(setCurrentFolder(undefined));
              } else {
                if (currentFolder === undefined) {
                  dispatch(setCurrentFolder(myFolder));
                }
              }

              setTooltipOpen(false);
            }}
            select
            SelectProps={{
              renderValue: (value) => {
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 700,
                        color: '#070154',
                      }}
                    >
                      {AppNameBannerMap[value as AppEnum]}
                    </Typography>
                  </div>
                );
              },
              MenuProps: {
                anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                MenuListProps: { disablePadding: true },
              },
            }}
            size="small"
          >
            {appOptions
              .filter((option) => option.featureFlag)
              .map(({ value, onClick }) => (
                <StyledMenuItem
                  key={value}
                  value={value}
                  onClick={
                    onClick
                      ? onClick
                      : () => {
                          dispatch(setCurrentFolder(myFolder));
                        }
                  }
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#070154' }}>
                      {AppNameBannerMap[value]}
                    </Typography>
                  </div>
                </StyledMenuItem>
              ))}
          </TextField>
        </Tooltip>
      )}
      {drawerOpen && (
        <DrawerToggleButton
          isDrawerOpen={drawerOpen}
          setIsDrawerOpen={handleDrawerToggle}
          tooltipCaption={'Close Panel'}
        />
      )}
    </DrawerAppsContainer>
  );
};

export default DrawerApps;
